<script setup lang="ts">
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import StatusBadge from '@/components/common/StatusBadge.vue'
import EditableNotesField from '@/components/rc/common/EditableNotesField.vue'
import { useWorkOrder } from '@/composables/rc/work-order'
import { useOIDCStore } from '@/stores/oidc.store'
import type { IWorkOrderUpdateForm } from '@/types/rc/forms'
import { WorkOrderStatus } from '@/types/rc/work-orders/WorkOrderStatus'
import { WorkOrderPriority } from '@/types/rc/work-orders/WorkOrderPriority'
import { Icon } from '@iconify/vue'
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'

const props = defineProps<{ workOrderNumber: string }>()

const oidcStore = useOIDCStore()
const { isManagerUser, isCustomer, profile } = storeToRefs(oidcStore)

const { workOrderDashboardSummaryQuery, updateWorkOrderMutation, isManageableByCurrentUser, isOutsourced } =
  useWorkOrder(props.workOrderNumber)
const { data, isPending, isError, error } = workOrderDashboardSummaryQuery()

// Refactor this into standalone component "updatable status"
const editingStatus = ref(false)
const selectedStatus = ref<WorkOrderStatus | undefined>(data.value?.work_order_information.status)
watch(data, (newValue) => {
  selectedStatus.value = newValue?.work_order_information.status
})

// Priority editing state
const editingPriority = ref(false)
const selectedPriority = ref<WorkOrderPriority | undefined>(data.value?.work_order_information.priority || undefined)
watch(data, (newValue) => {
  selectedPriority.value = newValue?.work_order_information.priority || undefined
})

const { mutate, isPending: mutationPending } = updateWorkOrderMutation

const onStatusChange = (status?: WorkOrderStatus) => {
  if (!selectedStatus.value) return
  mutate(
    { status },
    {
      onSettled: () => {
        editingStatus.value = false
      },
    }
  )
}

const onPriorityChange = (priority?: WorkOrderPriority) => {
  if (!selectedPriority.value) return
  mutate(
    { priority },
    {
      onSettled: () => {
        editingPriority.value = false
      },
    }
  )
}

const onPriorityClear = () => {
  mutate(
    { priority: null },
    {
      onSettled: () => {
        editingPriority.value = false
      },
    }
  )
}

const showCollapsedRow = ref(false)

const saveNotes = (text: string, discardNotes: () => void) => {
  const form: IWorkOrderUpdateForm = { notes: text }
  mutate(form, { onError: discardNotes })
}
</script>

<template>
  <el-row v-if="isPending">
    <el-col :span="6"><el-skeleton class="p-2" /></el-col>
    <el-col :span="6"><el-skeleton class="p-2" /></el-col>
    <el-col :span="6"><el-skeleton class="p-2" /></el-col>
    <el-col :span="6"><el-skeleton class="p-2" /></el-col>
  </el-row>
  <RlErrorAlert v-if="isError" :error="error" />
  <el-row v-if="data" class="py-2" :gutter="8">
    <el-col :span="6">
      <div class="flex flex-col gap-1">
        <label class="text-text-tertiary font-medium">Customer</label>
        <span v-if="!isManagerUser || isOutsourced">{{ data.work_order_information.customer.name }}</span>
        <span v-else>
          <RouterLink
            :to="{
              name: 'rc-customer-work-orders',
              params: { customerId: data.work_order_information.customer.id },
            }"
          >
            <el-button link class="!whitespace-normal !text-left !h-auto !p-0">
              <div class="inline-flex gap-2 items-center break-words">
                <span class="break-words">{{ data.work_order_information.customer.name }}</span>
                <Icon icon="material-symbols:open-in-new" :height="14" class="flex-shrink-0" />
              </div>
            </el-button>
          </RouterLink>
        </span>
      </div>
    </el-col>
    <el-col :span="6">
      <div class="flex flex-col gap-1">
        <label class="text-text-tertiary font-medium">Work order</label>
        <span>{{ data.work_order_information.number }}</span>
      </div>
    </el-col>
    <el-col :span="6">
      <div class="flex flex-col gap-1">
        <label class="text-text-tertiary font-medium">Start date</label>
        <span>{{ new Date(data.work_order_information.start_date).toLocaleDateString() }}</span>
      </div>
    </el-col>
    <el-col :span="4">
      <div class="flex flex-col gap-1">
        <label class="text-text-tertiary font-medium">Status</label>
        <!-- Displaying -->
        <span v-if="!editingStatus" class="flex gap-2">
          <StatusBadge :status="data.work_order_information.status" />
          <el-button v-if="isManagerUser && !isOutsourced" link @click="editingStatus = true">
            <Icon icon="material-symbols:edit-outline" :height="20" />
          </el-button>
        </span>
        <!-- Editing -->
        <span v-else class="text-nowrap flex gap-2">
          <el-select
            v-model="selectedStatus"
            class="w-4/5"
            :disabled="mutationPending"
            :loading="mutationPending"
            @click.stop
            @change="onStatusChange"
          >
            <el-option v-for="opt in Object.values(WorkOrderStatus)" :key="opt" :label="opt" :value="opt" />
          </el-select>

          <el-button v-if="isManageableByCurrentUser" :disabled="mutationPending" link @click="editingStatus = false">
            <Icon icon="material-symbols:close" :height="20" />
          </el-button>
        </span>
      </div>
    </el-col>
    <el-col :span="2">
      <div class="flex items-center justify-center h-full">
        <Icon
          icon="mdi:chevron-down"
          :height="40"
          class="bg-component-fill-badge m-4 p-2 rounded-full cursor-pointer transition-transform"
          :class="showCollapsedRow ? 'rotate-180' : ''"
          @click="showCollapsedRow = !showCollapsedRow"
        />
      </div>
    </el-col>
  </el-row>
  <el-collapse-transition>
    <el-row v-if="data" v-show="showCollapsedRow" :gutter="8">
      <el-col :span="6">
        <div class="flex flex-col gap-1">
          <label class="text-text-tertiary font-medium">Scope of work</label>
          <div class="flex flex-wrap gap-2">
            <el-tag v-for="svc in data.work_order_information.scope_of_work" :key="svc" size="small">{{ svc }}</el-tag>
          </div>
        </div>
      </el-col>
      <el-col :span="6">
        <div
          v-if="data.work_order_information.repair_request_number && isManagerUser && !isOutsourced"
          class="flex flex-col gap-1"
        >
          <label class="text-text-tertiary font-medium"> Repair Request </label>
          <span>
            <RouterLink
              :to="{
                name: 'rc-customer-repair-requests',
                params: { customerId: data.work_order_information.customer.id },
                query: { number: data.work_order_information.repair_request_number },
              }"
            >
              <el-button link>
                <div class="inline-flex gap-2 items-center">
                  {{ `#${data.work_order_information.repair_request_number.slice(-4)}` }}
                  <Icon icon="material-symbols:open-in-new" :height="14" />
                </div>
              </el-button>
            </RouterLink>
          </span>
        </div>
      </el-col>
      <el-col v-if="!isCustomer && data.work_order_information.tenant_id === profile?.tenant_id" :span="6">
        <div class="flex flex-col gap-1">
          <label class="text-text-tertiary font-medium">Priority</label>
          <!-- Displaying -->
          <span v-if="!editingPriority" class="flex gap-2">
            <StatusBadge v-if="data.work_order_information.priority" :status="data.work_order_information.priority" />
            <span v-else class="text-text-tertiary">-</span>
            <el-button v-if="isManagerUser && !isOutsourced" link @click="editingPriority = true">
              <Icon icon="material-symbols:edit-outline" :height="20" />
            </el-button>
          </span>
          <!-- Editing -->
          <span v-else class="text-nowrap flex gap-2">
            <el-select
              v-model="selectedPriority"
              class="w-4/5"
              :disabled="mutationPending"
              :loading="mutationPending"
              clearable
              @click.stop
              @clear="onPriorityClear"
              @change="onPriorityChange"
            >
              <el-option v-for="opt in Object.values(WorkOrderPriority)" :key="opt" :label="opt" :value="opt" />
            </el-select>

            <el-button
              v-if="isManageableByCurrentUser"
              :disabled="mutationPending"
              link
              @click="editingPriority = false"
            >
              <Icon icon="material-symbols:close" :height="20" />
            </el-button>
          </span>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="flex flex-col gap-1">
          <label class="text-text-tertiary font-medium">Notes</label>
          <EditableNotesField
            :cache-value="data.work_order_information.notes ?? ''"
            :placeholder="isManageableByCurrentUser ? 'Enter notes...' : 'No notes available'"
            :headless="!isManageableByCurrentUser"
            @save="saveNotes"
          />
        </div>
      </el-col>
    </el-row>
  </el-collapse-transition>
</template>
